import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import type { TransformStep } from "../../stores/equationPlayground";

interface StepAnimatorProps {
  steps: TransformStep[];
  originalEquation: string;
  currentIndex: number;
  onJumpTo: (index: number) => void;
  className?: string;
}

const spring = { type: "spring" as const, stiffness: 300, damping: 25 };

function StepAnimator({
  steps,
  originalEquation,
  currentIndex,
  onJumpTo,
  className,
}: StepAnimatorProps) {
  if (steps.length === 0) return null;

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <span className="text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)] mb-1">
        Steps
      </span>

      <motion.button
        onClick={() => onJumpTo(-1)}
        whileHover={{ backgroundColor: "var(--bg-surface-hover)" }}
        transition={spring}
        className={cn(
          "flex items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1 text-left text-[11px]",
          "transition-colors",
          currentIndex === -1
            ? "bg-[var(--color-lever-orange)]/8 text-[var(--color-lever-orange)] font-medium"
            : "text-[var(--text-secondary)]",
        )}
      >
        <span className="w-4 text-center text-[10px] font-mono">0</span>
        <span className="truncate font-mono">{originalEquation.slice(0, 40)}</span>
      </motion.button>

      <AnimatePresence>
        {steps.map((step, i) => (
          <motion.button
            key={step.id}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ ...spring, delay: i * 0.05 }}
            onClick={() => onJumpTo(i)}
            whileHover={{ backgroundColor: "var(--bg-surface-hover)" }}
            className={cn(
              "flex items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1 text-left text-[11px]",
              "transition-colors",
              i === currentIndex
                ? "bg-[var(--color-lever-orange)]/8 text-[var(--color-lever-orange)] font-medium"
                : "text-[var(--text-secondary)]",
            )}
          >
            <span className="w-4 text-center text-[10px] font-mono">{i + 1}</span>
            <ArrowRight size={8} weight="bold" className="shrink-0 text-[var(--text-muted)]" />
            <span className="truncate" title={step.explanation}>
              {step.transform}
            </span>
          </motion.button>
        ))}
      </AnimatePresence>
    </div>
  );
}

StepAnimator.displayName = "StepAnimator";

export { StepAnimator };
export type { StepAnimatorProps };
